import { motion } from "framer-motion";
import { FileText, SlidersHorizontal, Sparkles, Building2, CalendarClock, ClipboardCheck, ArrowRight } from "lucide-react";

const IMG = {
  particuliers:
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxzdW5saXQlMjBicmlnaHQlMjBjbGVhbiUyMGx1eHVyeSUyMGludGVyaW9yJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3OTMxODYyMzF8MA&ixlib=rb-4.1.0&q=80&w=1200",
  professionnels:
    "https://images.pexels.com/photos/245219/pexels-photo-245219.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const Journey = ({ id, reversed, kicker, title, desc, points, cta, img, imgAlt, testid, ctaTestid }) => (
  <section id={id} data-testid={testid} className="py-24 sm:py-32">
    <div className={`max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className={reversed ? "lg:order-2" : ""}
      >
        <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold mb-4">{kicker}</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">{title}</h2>
        <p className="mt-5 text-base text-slate-300 leading-relaxed">{desc}</p>
        <ul className="mt-8 space-y-4">
          {points.map((p) => (
            <li key={p.label} className="flex items-start gap-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-amber-400/10 border border-amber-400/25 text-amber-300 shrink-0">
                <p.icon className="w-5 h-5" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-sm font-semibold text-white">{p.label}</p>
                <p className="text-sm text-slate-400 mt-0.5">{p.text}</p>
              </div>
            </li>
          ))}
        </ul>
        <a
          href="#devis"
          data-testid={ctaTestid}
          className="group mt-9 inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold text-[#0A0D14] transition-all duration-300 hover:bg-amber-300 hover:scale-[1.03] active:scale-[0.97] hover:shadow-[0_0_35px_rgba(242,193,78,0.45)]"
        >
          {cta}
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className={`relative ${reversed ? "lg:order-1" : ""}`}
      >
        <div className="absolute -inset-3 rounded-[2rem] border border-amber-400/20 pointer-events-none" />
        <img
          src={img}
          alt={imgAlt}
          loading="lazy"
          className="w-full aspect-[4/3] object-cover rounded-3xl border border-white/10"
        />
      </motion.div>
    </div>
  </section>
);

export const Journeys = () => (
  <div className="bg-[#0D1220]">
    <Journey
      id="particuliers"
      testid="journey-particuliers"
      ctaTestid="journey-cta-particuliers"
      kicker="Espace particuliers"
      title="Votre intérieur, entre de bonnes mains"
      desc="Ménage de votre logement, nettoyage des vitres, nettoyage intérieur de votre véhicule, remise en état après travaux ou déménagement : dites-nous simplement ce dont vous avez besoin, DIMORA s'occupe du reste."
      points={[
        { icon: FileText, label: "Devis simple et rapide", text: "Quelques informations suffisent : vous recevez une proposition claire, sans engagement." },
        { icon: SlidersHorizontal, label: "Adaptation à vos besoins", text: "Intervention ponctuelle ou passage régulier, selon votre rythme et votre logement." },
        { icon: Sparkles, label: "Qualité d'exécution", text: "Un travail soigné, dans le respect de votre intérieur et de vos consignes." },
      ]}
      cta="Demander mon devis"
      img={IMG.particuliers}
      imgAlt="Séjour lumineux parfaitement entretenu"
    />
    <Journey
      id="professionnels"
      testid="journey-professionnels"
      ctaTestid="journey-cta-professionnels"
      reversed
      kicker="Espace professionnels"
      title="Des locaux impeccables, sans y penser"
      desc="Entreprises, commerces, bureaux et locaux professionnels : DIMORA organise l'entretien de vos espaces, en intervention ponctuelle ou régulière, selon votre cahier des charges. Également : vitres, fin de chantier et intérieur de véhicules professionnels, selon les capacités de DIMORA."
      points={[
        { icon: CalendarClock, label: "Ponctuel ou régulier", text: "Remise en état, fin de chantier, vitres ou entretien programmé : le rythme s'adapte à votre activité." },
        { icon: ClipboardCheck, label: "Respect du cahier des charges", text: "Chaque intervention suit vos consignes : zones, horaires, contraintes d'accès." },
        { icon: Building2, label: "Étude de vos besoins", text: "Décrivez votre site et vos attentes : nous étudions votre demande et construisons une proposition adaptée." },
      ]}
      cta="Étudier mon besoin"
      img={IMG.professionnels}
      imgAlt="Bureaux professionnels entretenus"
    />
  </div>
);
